/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import { services } from "../../services";
import { actions } from "../../actions";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/use-toast";
import CustomLoader from "../ui/custom-loader";
import {
  ApiResponse,
  KYCContract,
  ComplianceContract,
  ApproveViewButtonsProps,
} from "../../types/users/approve.view.buttons";

const ApproveViewButtons: React.FC<ApproveViewButtonsProps> = ({
  selectedIds,
  resource,
  onSuccess,
  onError,
}) => {
  const [loading, setLoading] = useState(false);
  const [kycContract, setKYCContract] = useState<KYCContract | null>(null);
  const [complianceContract, setComplianceContract] =
    useState<ComplianceContract | null>(null);
  const [adminRoleHash, setAdminRoleHash] = useState<string>("");
  const [web3Data, setWeb3Data] = useState<any>(null);

  useEffect(() => {
    async function initializeContracts() {
      try {
        const web3Data = await services.getWeb3();
        setWeb3Data(web3Data);

        const kycInstance = await actions.getKycVerificationContract();
        const complianceInstance =
          await actions.getComplianceRegistryContract();

        setKYCContract(kycInstance as unknown as KYCContract);
        setComplianceContract(
          complianceInstance as unknown as ComplianceContract,
        );

        const adminRoleHash = await kycInstance.methods.ADMIN_ROLE().call();
        if (typeof adminRoleHash === "string") {
          setAdminRoleHash(adminRoleHash);
        }
      } catch (error) {
        toast({
          variant: "destructive",
          description: "Error initializing contracts",
        });
      }
    }
    initializeContracts();
  }, []);

  const handleApprove = async () => {
    if (loading) return;
    setLoading(true);

    try {
      const params = {
        userIds: selectedIds,
        status: "APPROVED",
      };

      const response = (await services.post(
        "/users/approve",
        params,
      )) as ApiResponse;

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === 200
      ) {
        setLoading(false);
        if (
          response.data &&
          typeof response.data === "object" &&
          response.data.message
        ) {
          toast({
            description: response.data.message,
          });
        } else {
          toast({
            description: "Users approved successfully",
          });
        }
        onSuccess?.();
      } else {
        throw new Error("Failed to approve users");
      }
    } catch (error) {
      setLoading(false);
      toast({
        variant: "destructive",
        description: "Error approving users",
      });
      onError?.(error);
    }
  };

  const onTransaction = async (walletAddresses: string[]): Promise<boolean> => {
    if (!web3Data || !kycContract || !complianceContract || !adminRoleHash) {
      toast({
        variant: "destructive",
        description: "Contract not initialized properly",
      });
      return false;
    }

    try {
      const hasAdminRole = await kycContract.methods
        .hasRole(adminRoleHash, web3Data.accounts[0])
        .call();

      if (!hasAdminRole) {
        toast({
          variant: "destructive",
          description: "Current wallet does not have admin role",
        });
        return false;
      }

      for (const walletAddress of walletAddresses) {
        const metadata = JSON.stringify({ approved: true });
        const country = "US";
        const kycStatus = 1;

        // KYC Verification
        await kycContract.methods
          .setVerificationData(walletAddress, metadata, country, kycStatus)
          .send({ from: web3Data.accounts[0] });

        toast({
          description: "KYC verification complete",
        });

        // Compliance Update
        await complianceContract.methods
          .updateComplianceStatus(walletAddress, true, metadata)
          .send({ from: web3Data.accounts[0] });

        toast({
          description: "Compliance update complete",
        });
      }

      return true;
    } catch (error: any) {
      setLoading(false);
      toast({
        variant: "destructive",
        description: `Transaction failed: ${error.message || error}`,
      });
      return false;
    }
  };

  const updateMany = (): void => {
    if (!selectedIds || selectedIds.length === 0) {
      toast({
        variant: "default",
        description: "Select at least one user first",
      });
      return;
    }

    const walletAddresses = selectedIds;
    if (walletAddresses.length > 0) {
      onTransaction(walletAddresses);
    } else {
      toast({
        variant: "default",
        description: "No valid pending creator wallet addresses found",
      });
    }
  };

  return (
    <Button
      variant="default"
      onClick={handleApprove}
      disabled={loading || selectedIds.length === 0}
      className="flex items-center gap-2"
    >
      {loading ? (
        <>
          <CustomLoader size="sm" />
          <span>Approving...</span>
        </>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
          <span>Approve Selected</span>
        </>
      )}
    </Button>
  );
};

export default ApproveViewButtons;
